// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Switch, View as RNView } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import GoffyInput from '@/components/GoffyInput';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import CoursePicker from '@/features/round-init/CoursePicker';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { sendAndAwaitAck } from '@/services/wsAck';
import { formatTeeTime } from '@/features/round-scheduling/format';
import type { Course } from '@/features/round-init/types';
import appColors from '@/theme/colors';
import { t } from '@/i18n';

const HOLES_OPTIONS = [18, 9] as const;

export default function ScheduleRoundScreen() {
  const { tourId: tourIdParam } = useLocalSearchParams<{ tourId?: string }>();
  const routeTourId = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
  const isSingleRound = routeTourId === 'single-round';
  const { user } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const [playerId, setPlayerId] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [teeTime, setTeeTime] = useState<Date>(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    return now;
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [holesCount, setHolesCount] = useState<number>(18);
  const [name, setName] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve the player id so the course picker can offer recently played courses.
  useEffect(() => {
    if (!user?.id || !user.token) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as { id?: string };
        if (!cancelled) setPlayerId(payload.id ?? null);
      } catch {
        /* recents are optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.id, user?.token]);

  const handleSelectCourse = useCallback((_id: string, _name: string, course: Course) => {
    setSelectedCourse(course);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!user?.token) return;
    if (!selectedCourse) {
      setError(t('schedule.missingCourse'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);

      // Single-round scheduling: mint the hidden tour first, exactly like round-init does, then
      // schedule the round under it.
      let tourId = routeTourId ?? null;
      if (isSingleRound) {
        const created = await sendAndAwaitAck(
          socket,
          {
            type: 'tour.initiate',
            tourName: t('players.singleRoundHiddenTourName'),
            playerIds: [],
            isHidden: true,
          },
          ['tour.created']
        );
        tourId = (created.tourId ?? created.TourId) as string | null;
        if (!tourId) {
          throw new Error(t('schedule.error'));
        }
      }

      const result = await sendAndAwaitAck(
        socket,
        {
          type: 'round.schedule',
          tourId,
          courseId: selectedCourse.id,
          courseName: selectedCourse.name,
          courseAddress: selectedCourse.address,
          courseLat: selectedCourse.lat,
          courseLon: selectedCourse.lon,
          holesCount,
          teeTimeUtc: teeTime.toISOString(),
          // Email is opt-in; in-app notifications always fire regardless of this flag.
          emailNotifications,
          // Optional: lets the backend auto-accept the scheduler (not just the tour creator) as
          // organizer for multi-member tour rounds. Backend falls back to the creator if absent.
          ...(playerId ? { organizerPlayerId: playerId } : {}),
          ...(name.trim() ? { name: name.trim() } : {}),
        },
        ['round.scheduleUpdated', 'round.schedule.rejected']
      );

      if (result.type === 'round.schedule.rejected') {
        const message = typeof result.message === 'string' ? result.message : '';
        setError(message || t('schedule.rejected'));
        setSubmitting(false);
        return;
      }

      const roundId = (result.roundId ?? result.RoundId) as string | undefined;
      if (roundId) {
        router.replace(`/round/${roundId}/rsvp`);
        return;
      }
      router.back();
    } catch {
      setError(t('schedule.error'));
      setSubmitting(false);
    }
  }, [apiBaseUrl, emailNotifications, holesCount, isSingleRound, name, playerId, routeTourId, router, selectedCourse, teeTime, user?.token]);

  return (
    <RNView style={styles.pageBackground}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.container}>
          <Text style={styles.title}>{t('schedule.heading')}</Text>

          <CoursePicker
            selectedCourseId={selectedCourse?.id ?? null}
            selectedCourseName={selectedCourse?.name ?? ''}
            onSelect={handleSelectCourse}
            token={user?.token}
            playerId={playerId}
          />

          <View style={styles.card}>
            <Text style={styles.label}>{t('schedule.teeTimeLabel')}</Text>
            <Pressable style={styles.teeTimeButton} onPress={() => setDatePickerOpen(true)}>
              <Text style={styles.teeTimeText}>{formatTeeTime(teeTime.toISOString())}</Text>
              <Text style={styles.teeTimeHint}>{t('schedule.pickTeeTime')}</Text>
            </Pressable>
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>{t('schedule.holesLabel')}</Text>
            <View style={styles.holesRow}>
              {HOLES_OPTIONS.map((option) => (
                <Pressable
                  key={option}
                  style={[styles.holesOption, holesCount === option && styles.holesOptionActive]}
                  onPress={() => setHolesCount(option)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: holesCount === option }}
                >
                  <Text style={[styles.holesOptionText, holesCount === option && styles.holesOptionTextActive]}>
                    {option === 18 ? t('schedule.holes18') : t('schedule.holes9')}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>{t('schedule.nameLabel')}</Text>
            <GoffyInput value={name} onChangeText={setName} placeholder={t('schedule.namePlaceholder')} />
          </View>

          <View style={styles.card}>
            <View style={styles.notifyRow}>
              <Text style={styles.notifyLabel}>{t('schedule.notifyByEmail')}</Text>
              <Switch
                value={emailNotifications}
                onValueChange={setEmailNotifications}
                trackColor={{ true: '#A1D493', false: '#1A3A20' }}
                accessibilityLabel={t('schedule.notifyByEmail')}
                testID="schedule-notify-email"
              />
            </View>
            <Text style={styles.notifyHint}>{t('schedule.notifyByEmailHint')}</Text>
          </View>

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <GoffyButton
            label={submitting ? t('schedule.submitting') : t('schedule.submit')}
            onPress={() => void handleSubmit()}
            disabled={submitting}
            tone="accent"
            testID="schedule-submit"
          />
        </View>
      </ScrollView>

      <PlatformDatePicker
        open={datePickerOpen}
        date={teeTime}
        mode="datetime"
        onConfirm={(value) => {
          setTeeTime(value);
          setDatePickerOpen(false);
        }}
        onCancel={() => setDatePickerOpen(false)}
      />
    </RNView>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  scrollContent: {
    padding: 16,
  },
  container: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    gap: 14,
  },
  title: {
    color: appColors.text.heading,
    fontSize: 24,
    fontWeight: '800',
  },
  card: {
    borderRadius: 16,
    padding: 18,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 10,
  },
  label: {
    color: appColors.text.primary,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontSize: 13,
  },
  teeTimeButton: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#07200D',
  },
  teeTimeText: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '600',
  },
  teeTimeHint: {
    color: '#7FA978',
    fontSize: 12,
    marginTop: 2,
  },
  holesRow: {
    flexDirection: 'row',
    columnGap: 10,
  },
  holesOption: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#0B2A14',
  },
  holesOptionActive: {
    borderColor: '#A1D493',
    backgroundColor: '#1A3A20',
  },
  holesOptionText: {
    color: '#C8C8B0',
    fontWeight: '700',
  },
  holesOptionTextActive: {
    color: appColors.text.primary,
  },
  notifyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  notifyLabel: {
    color: appColors.text.primary,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontSize: 13,
    flexShrink: 1,
  },
  notifyHint: {
    color: '#7FA978',
    fontSize: 12,
  },
  error: {
    color: '#ff6666',
  },
});
